import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  KeyRound,
  ShieldCheck,
  Lock,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  RefreshCw,
  LogOut,
  Mail,
  Server,
  Check,
} from "lucide-react";

const API_BASE = "https://api.vanyacollectionsrajahmundry.com";

const AdminSettingsPage = () => {
  const navigate = useNavigate();

  // Active admin session data
  const [adminEmail, setAdminEmail] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  // Form fields
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Visibility toggles
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Status & feedback
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error', message: '' }

  // Load active admin info
  useEffect(() => {
    try {
      localStorage.removeItem("admin_custom_credentials");
      const stored = localStorage.getItem("admin");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.email) setAdminEmail(parsed.email);
        if (parsed.passwordChangedAt) setLastUpdated(parsed.passwordChangedAt);
      } else {
        setAdminEmail("admin@vanyacollections.com");
      }
    } catch (e) {
      setAdminEmail("admin@vanyacollections.com");
    }
  }, []);

  // Compute password strength
  const getPasswordStrength = (pwd) => {
    if (!pwd) return { label: "None", percent: 0, color: "#cbd5e1" };
    if (pwd.length < 6) return { label: "Too Short", percent: 25, color: "#ef4444" };
    let score = 1;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    if (pwd.length >= 8) score++;

    if (score <= 2) return { label: "Weak", percent: 50, color: "#f59e0b" };
    if (score <= 4) return { label: "Medium", percent: 75, color: "#3b82f6" };
    return { label: "Strong", percent: 100, color: "#10b981" };
  };

  const strength = getPasswordStrength(newPassword);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setFeedback(null);

    // Validation
    if (!currentPassword) {
      setFeedback({ type: "error", message: "Please enter your current login password." });
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      setFeedback({
        type: "error",
        message: "New password must be at least 6 characters in length.",
      });
      return;
    }

    if (newPassword === currentPassword) {
      setFeedback({
        type: "error",
        message: "New password cannot be the same as your current password.",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setFeedback({
        type: "error",
        message: "New password and confirmation password do not match.",
      });
      return;
    }

    setSaving(true);

    try {
      const cleanEmail = (adminEmail || "").trim().toLowerCase() || "admin@vanyacollections.com";
      const now = new Date().toISOString();

      // 1. Update password directly in PostgreSQL database
      const changeRes = await fetch(`${API_BASE}/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: cleanEmail,
          currentPassword,
          newPassword,
        }),
      });

      const changeData = await changeRes.json().catch(() => ({}));

      if (!changeRes.ok) {
        throw new Error(changeData.message || "Failed to update password in database.");
      }

      // 2. Verify new password authentication with backend database
      const loginCheckRes = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: cleanEmail, password: newPassword }),
      });
      const loginCheckData = await loginCheckRes.json().catch(() => ({}));

      if (!loginCheckRes.ok || !loginCheckData.user) {
        throw new Error(
          loginCheckData.message || "Database update verification failed. Please check your credentials."
        );
      }

      // 3. Purge any stale legacy credentials and update session
      try {
        localStorage.removeItem("admin_custom_credentials");
      } catch (e) {}

      let adminObj = {};
      try {
        adminObj = JSON.parse(localStorage.getItem("admin") || "{}");
      } catch (e) {
        adminObj = {};
      }

      localStorage.setItem(
        "admin",
        JSON.stringify({
          ...adminObj,
          email: cleanEmail,
          passwordChangedAt: now,
        })
      );

      setLastUpdated(now);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");

      setFeedback({
        type: "success",
        message: "Admin password successfully saved and verified in the database!",
      });

      setTimeout(() => setFeedback(null), 6000);
    } catch (err) {
      console.error("Change password error:", err);
      setFeedback({
        type: "error",
        message: err.message || "Failed to update password. Please check your current password and try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin");
    navigate("/login");
  };

  return (
    <div style={styles.container}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <div style={styles.badgeLabel}>
            <Sparkles size={13} color="#D4AF37" />
            <span>SYSTEM SECURITY & PREFERENCES</span>
          </div>
          <h1 style={styles.title}>Admin Settings</h1>
          <p style={styles.subtitle}>
            Manage administrator credentials, change login password, and monitor authentication security.
          </p>
        </div>

        <button onClick={handleLogout} style={styles.logoutBtn} title="Sign out of Admin Panel">
          <LogOut size={16} />
          <span>Logout</span>
        </button>
      </div>

      {/* FEEDBACK ALERT */}
      {feedback && (
        <div
          style={{
            ...styles.feedbackAlert,
            backgroundColor: feedback.type === "success" ? "#ecfdf5" : "#fef2f2",
            borderColor: feedback.type === "success" ? "#a7f3d0" : "#fecaca",
            color: feedback.type === "success" ? "#065f46" : "#991b1b",
          }}
        >
          {feedback.type === "success" ? (
            <CheckCircle2 size={20} color="#059669" />
          ) : (
            <AlertCircle size={20} color="#dc2626" />
          )}
          <span>{feedback.message}</span>
        </div>
      )}

      {/* MAIN LAYOUT */}
      <div className="admin-settings-grid" style={styles.grid}>
        {/* LEFT CARD: CHANGE PASSWORD FORM */}
        <div className="admin-card-responsive" style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.cardIcon}>
              <KeyRound size={20} color="#D4AF37" />
            </div>
            <div>
              <h2 style={styles.cardTitle}>Change Admin Password</h2>
              <p style={styles.cardSubtitle}>
                Update the password used to log in to the Vanya Collections administrative portal.
              </p>
            </div>
          </div>

          <form onSubmit={handlePasswordChange} style={styles.form}>
            {/* Email (Readonly or editable reference) */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                <span>Admin Login Email</span>
              </label>
              <div style={styles.inputWrapper}>
                <Mail size={17} style={styles.inputIcon} />
                <input
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="admin@vanyacollections.com"
                  style={{ ...styles.input, backgroundColor: "#f8fafc" }}
                />
              </div>
              <span style={styles.hint}>Email address associated with this administrator account</span>
            </div>

            {/* Current Password */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                <span>Current Password</span>
                <span style={styles.requiredStar}>*</span>
              </label>
              <div style={styles.inputWrapper}>
                <Lock size={17} style={styles.inputIcon} />
                <input
                  type={showCurrent ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  required
                  style={styles.input}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  style={styles.eyeBtn}
                >
                  {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                <span>New Password</span>
                <span style={styles.requiredStar}>*</span>
              </label>
              <div style={styles.inputWrapper}>
                <Lock size={17} style={styles.inputIcon} />
                <input
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min. 6 characters)"
                  required
                  style={styles.input}
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  style={styles.eyeBtn}
                >
                  {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              {/* Strength bar */}
              {newPassword && (
                <div style={{ marginTop: 6 }}>
                  <div style={styles.strengthBarBg}>
                    <div
                      style={{
                        ...styles.strengthBarFill,
                        width: `${strength.percent}%`,
                        backgroundColor: strength.color,
                      }}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                    <span style={{ fontSize: "11px", color: "#64748b" }}>Password strength</span>
                    <span style={{ fontSize: "11px", fontWeight: "700", color: strength.color }}>
                      {strength.label}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Confirm New Password */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                <span>Confirm New Password</span>
                <span style={styles.requiredStar}>*</span>
              </label>
              <div style={styles.inputWrapper}>
                <Lock size={17} style={styles.inputIcon} />
                <input
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  required
                  style={styles.input}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  style={styles.eyeBtn}
                >
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <span style={{ fontSize: "11.5px", color: "#ef4444" }}>Passwords do not match</span>
              )}
            </div>

            {/* Checklist */}
            <div style={styles.checklist}>
              <div style={styles.checkItem}>
                {newPassword.length >= 6 ? (
                  <Check size={14} color="#10b981" />
                ) : (
                  <span style={styles.checkDot} />
                )}
                <span style={{ color: newPassword.length >= 6 ? "#065f46" : "#64748b" }}>
                  Minimum 6 characters long
                </span>
              </div>
              <div style={styles.checkItem}>
                {confirmPassword && newPassword === confirmPassword ? (
                  <Check size={14} color="#10b981" />
                ) : (
                  <span style={styles.checkDot} />
                )}
                <span
                  style={{
                    color:
                      confirmPassword && newPassword === confirmPassword ? "#065f46" : "#64748b",
                  }}
                >
                  Both passwords match identically
                </span>
              </div>
            </div>

            {/* Action buttons */}
            <div style={styles.actionRow}>
              <button
                type="button"
                onClick={() => {
                  setCurrentPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                  setFeedback(null);
                }}
                style={styles.resetBtn}
              >
                Clear
              </button>

              <button
                type="submit"
                disabled={saving}
                style={{
                  ...styles.submitBtn,
                  opacity: saving ? 0.75 : 1,
                  cursor: saving ? "not-allowed" : "pointer",
                }}
              >
                {saving ? (
                  <>
                    <RefreshCw size={17} className="spin-icon" />
                    <span>Updating Password...</span>
                  </>
                ) : (
                  <>
                    <Lock size={17} />
                    <span>Update Password</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* RIGHT CARD: ACCOUNT OVERVIEW & SECURITY ADVISORY */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Admin Identity Card */}
          <div style={styles.card}>
            <div style={styles.profileHeader}>
              <div style={styles.avatarMedallion}>
                <span style={{ fontSize: "20px", fontWeight: "800", color: "#14352b" }}>V</span>
              </div>
              <div>
                <h3 style={styles.profileName}>Super Administrator</h3>
                <span style={styles.profileEmail}>{adminEmail}</span>
              </div>
            </div>

            <div style={styles.detailsList}>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Role Access</span>
                <span style={styles.roleBadge}>FULL SUPERADMIN</span>
              </div>

              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Security Status</span>
                <span style={styles.statusActive}>
                  <span style={styles.activeDot} />
                  Active & Verified
                </span>
              </div>

              {lastUpdated && (
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Last Password Change</span>
                  <span style={{ fontSize: "12px", color: "#334155", fontWeight: "600" }}>
                    {new Date(lastUpdated).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Security Best Practices Card */}
          <div style={styles.card}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
              <ShieldCheck size={20} color="#14352b" />
              <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "800", color: "#14352b" }}>
                Security Best Practices
              </h3>
            </div>

            <ul style={styles.securityList}>
              <li>Use a strong password with a combination of uppercase letters, numbers, and symbols.</li>
              <li>Avoid using the same password across personal email and store admin access.</li>
              <li>Always log out when accessing the administrative portal on shared computers.</li>
              <li>Keep your UPI QR code settings and courier rules reviewed regularly.</li>
            </ul>

            <div style={styles.backendNotice}>
              <Server size={15} color="#059669" style={{ flexShrink: 0 }} />
              <div>
                <strong style={{ display: "block", color: "#065f46", fontSize: "12px" }}>
                  Production API Synchronized
                </strong>
                <span style={{ fontSize: "11px", color: "#047857" }}>
                  Connected to https://api.vanyacollectionsrajahmundry.com
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin-icon {
          animation: spin 1s linear infinite;
        }

        @media (max-width: 900px) {
          .admin-settings-grid {
            grid-template-columns: 1fr !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 20px !important;
          }
          .admin-card-responsive {
            padding: 20px 16px !important;
            border-radius: 14px !important;
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: "1100px",
    margin: "0 auto",
    fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "24px",
    flexWrap: "wrap",
    gap: "16px",
  },
  badgeLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "1.2px",
    color: "#D4AF37",
    backgroundColor: "#14352b",
    padding: "4px 10px",
    borderRadius: "20px",
    marginBottom: "8px",
  },
  title: {
    fontSize: "26px",
    fontWeight: "800",
    color: "#14352b",
    margin: "0 0 6px 0",
  },
  subtitle: {
    fontSize: "14px",
    color: "#64748b",
    margin: 0,
    maxWidth: "650px",
  },
  logoutBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "9px 16px",
    borderRadius: "10px",
    backgroundColor: "#ffffff",
    border: "1.5px solid #e2e8f0",
    color: "#dc2626",
    fontSize: "13px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
  },
  feedbackAlert: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "14px 18px",
    borderRadius: "10px",
    border: "1px solid",
    marginBottom: "22px",
    fontSize: "14px",
    fontWeight: "600",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 380px",
    gap: "24px",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "26px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 6px 18px rgba(0,0,0,0.04)",
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: "14px",
    paddingBottom: "18px",
    marginBottom: "22px",
    borderBottom: "1px solid #f1f5f9",
  },
  cardIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    backgroundColor: "#14352b",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  cardTitle: {
    fontSize: "18px",
    fontWeight: "800",
    color: "#14352b",
    margin: "0 0 4px 0",
  },
  cardSubtitle: {
    fontSize: "13px",
    color: "#64748b",
    margin: 0,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#334155",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  requiredStar: {
    color: "#ef4444",
  },
  inputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  inputIcon: {
    position: "absolute",
    left: "14px",
    color: "#94a3b8",
    pointerEvents: "none",
  },
  input: {
    width: "100%",
    padding: "12px 42px 12px 42px",
    borderRadius: "10px",
    border: "1.5px solid #cbd5e1",
    fontSize: "14px",
    color: "#0f172a",
    backgroundColor: "#ffffff",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  },
  eyeBtn: {
    position: "absolute",
    right: "12px",
    background: "transparent",
    border: "none",
    color: "#94a3b8",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
  },
  hint: {
    fontSize: "11px",
    color: "#94a3b8",
  },
  strengthBarBg: {
    height: "5px",
    backgroundColor: "#e2e8f0",
    borderRadius: "3px",
    overflow: "hidden",
  },
  strengthBarFill: {
    height: "100%",
    transition: "width 0.3s ease, background-color 0.3s ease",
  },
  checklist: {
    backgroundColor: "#f8fafc",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #edf2f7",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  checkItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
  },
  checkDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#cbd5e1",
  },
  actionRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "8px",
  },
  resetBtn: {
    padding: "12px 20px",
    borderRadius: "10px",
    backgroundColor: "#f8fafc",
    border: "1.5px solid #cbd5e1",
    color: "#475569",
    fontSize: "14px",
    fontWeight: "700",
    cursor: "pointer",
  },
  submitBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 26px",
    borderRadius: "10px",
    background: "linear-gradient(135deg, #14352b 0%, #1f4a3c 100%)",
    color: "#D4AF37",
    border: "none",
    fontSize: "14px",
    fontWeight: "800",
    boxShadow: "0 4px 14px rgba(20, 53, 43, 0.25)",
  },
  profileHeader: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    paddingBottom: "18px",
    borderBottom: "1px solid #f1f5f9",
    marginBottom: "16px",
  },
  avatarMedallion: {
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #f5d470 0%, #d4af37 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 10px rgba(212, 175, 55, 0.3)",
    flexShrink: 0,
  },
  profileName: {
    fontSize: "16px",
    fontWeight: "800",
    color: "#14352b",
    margin: "0 0 3px 0",
  },
  profileEmail: {
    fontSize: "12.5px",
    color: "#64748b",
  },
  detailsList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  detailItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: "10px",
    borderBottom: "1px solid #f8fafc",
  },
  detailLabel: {
    fontSize: "12px",
    color: "#64748b",
    fontWeight: "500",
  },
  roleBadge: {
    fontSize: "10px",
    fontWeight: "800",
    backgroundColor: "#14352b",
    color: "#D4AF37",
    padding: "3px 8px",
    borderRadius: "12px",
    letterSpacing: "0.5px",
  },
  statusActive: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    fontWeight: "700",
    color: "#065f46",
    backgroundColor: "#ecfdf5",
    padding: "3px 8px",
    borderRadius: "12px",
  },
  activeDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#10b981",
  },
  securityList: {
    margin: "0 0 16px 0",
    paddingLeft: "18px",
    fontSize: "12.5px",
    color: "#475569",
    lineHeight: "1.6",
  },
  backendNotice: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    backgroundColor: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: "10px",
    padding: "10px 12px",
  },
};

export default AdminSettingsPage;
